import React, { useState, useEffect } from "react";

const API_BASE = "https://nissi-india.com"; // change to env if needed

const ShutteringMaterialsReportPrint = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [reportParams, setReportParams] = useState({
    assetSubType: "Select",
    size: "",
    make: "Select",
    fromDate: "",
    toDate: "",
    project: "SRC PROJECTS PRIVATE LIMITED",
  });

  // ✅ Extract Parameters from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const params = {
      assetSubType: urlParams.get("assetSubType") || "Select",
      size: urlParams.get("size") || "",
      make: urlParams.get("make") || "Select",
      fromDate: urlParams.get("fromDate") || "",
      toDate: urlParams.get("toDate") || "",
      project: urlParams.get("project") || "SRC PROJECTS PRIVATE LIMITED",
    };

    setReportParams(params);
  }, []);

  // ✅ Fetch Data from VIEW API
  const fetchReportData = async (params) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/shutteringReport/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          assetSubType: params.assetSubType,
          size: params.size,
          make: params.make,
          fromDate: params.fromDate,
          toDate: params.toDate,
        }),
      });

      const json = await res.json();

      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to load report");
      }

      // ✅ Map response fields if needed
      const mapped = (json.data || []).map((row, index) => ({
        sno: row.SNo || index + 1,
        assetType: row.AssetType || row.fld_AssetSubTypeName || "",
        size: row.Size || row.fld_size || "",
        totalQty: row.TotalQty || 0,
        sgs: row.SGS || 0,
        rns: row.RNS || 0,
        fmp: row.FMP || 0,
        aan: row.AAN || 0,
        srcl: row.SRCL || 0,
        damiler: row.DAMILER || 0,
        dormancy: row.DORMANCY || 0,
        estagmp: row.ESTAGMP || 0,
        gestAmp: row.GESTAMP || 0,
        kpakPer: row.KPAKPER || row["K.PAK PER"] || 0,
        mot: row.MOT || 0,
        totalValue: row.TotalValue || 0,
      }));

      setReportData(mapped);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch when parameters updated
  useEffect(() => {
    if (!reportParams) return;
    fetchReportData(reportParams);
  }, [reportParams]);

  const totalValue = reportData.reduce(
    (sum, item) => sum + (parseFloat(item.totalValue) || 0),
    0
  );

  return (
    <div className="max-w-full mx-auto p-4 bg-white font-sans text-xs print:p-0 print:max-w-none">
      {/* Header */}
      <div className="border-2 border-black mb-0">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">{reportParams.project}</h1>
        </div>
        <div className="text-center py-2 border-b border-black font-bold">
          SHUTTERING MATERIALS OVERALL REPORT
        </div>
      </div>

      {/* Loading/Error */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          <div className="mt-2">Loading report data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <div className="font-bold">Error loading report:</div>
          <div>{error}</div>
          <button
            onClick={() => fetchReportData(reportParams)}
            className="mt-2 bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border-l-2 border-r-2 border-b-2 border-black">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black px-1 py-2 w-8">S.No</th>
              <th className="border-r border-black px-1 py-2 w-20">Asset Type</th>
              <th className="border-r border-black px-1 py-2 w-12">Size</th>
              <th className="border-r border-black px-1 py-2 w-12">Total Qty</th>
              <th className="border-r border-black px-1 py-2 w-8">SGS</th>
              <th className="border-r border-black px-1 py-2 w-8">RNS</th>
              <th className="border-r border-black px-1 py-2 w-8">FMP</th>
              <th className="border-r border-black px-1 py-2 w-8">AAN</th>
              <th className="border-r border-black px-1 py-2 w-8">SRCL</th>
              <th className="border-r border-black px-1 py-2 w-12">DAMILER</th>
              <th className="border-r border-black px-1 py-2 w-12">DORMANCY</th>
              <th className="border-r border-black px-1 py-2 w-12">ESTAGMP</th>
              <th className="border-r border-black px-1 py-2 w-12">GESTAMP</th>
              <th className="border-r border-black px-1 py-2 w-12">K.PAK PER</th>
              <th className="border-r border-black px-1 py-2 w-8">MOT</th>
              <th className="border-r border-black px-1 py-2 w-16">Total Value</th>
            </tr>
          </thead>
          <tbody>
            {!loading && reportData.length === 0 ? (
              <tr>
                <td colSpan="16" className="p-4 text-center text-gray-500 border-r border-black">
                  No data available
                </td>
              </tr>
            ) : (
              reportData.map((item, index) => (
                <tr key={index} className="border-b border-gray-300">
                  <td className="border-r border-black px-1 py-1 text-center">{item.sno}</td>
                  <td className="border-r border-black px-1 py-1">{item.assetType}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.size}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.totalQty}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.sgs}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.rns}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.fmp}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.aan}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.srcl}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.damiler}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.dormancy}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.estagmp}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.gestAmp}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.kpakPer}</td>
                  <td className="border-r border-black px-1 py-1 text-center">{item.mot}</td>
                  <td className="border-r border-black px-1 py-1 text-right">
                    {Number(item.totalValue).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))
            )}

            {reportData.length > 0 && (
              <tr className="border-t-2 border-black font-bold">
                <td colSpan="15" className="border-r border-black px-1 py-1 text-right">
                  Total:
                </td>
                <td className="px-1 py-1 text-right">
                  {totalValue.toLocaleString("en-IN")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="mt-4 text-xs text-gray-600 border-t border-gray-300 pt-2">
        <div className="underline">
          Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
        </div>
      </div>
    </div>
  );
};

export default ShutteringMaterialsReportPrint;
